import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import NeoLoader from './NeoLoader';
import NeoButton from './NeoButton';
import NeoInput from './NeoInput';
import NeoEmptyState from './NeoEmptyState';

const NeoTable = ({
    columns = [],
    data = [],
    keyField = 'id',
    loading = false,
    isLoading = false,
    emptyMessage = 'Nenhum registro encontrado',
    emptySubMessage = 'Use os formulários para cadastrar novas informações.',
    itemsPerPageOptions = [5, 10, 20, 50],
    defaultItemsPerPage = 5,
    onRowClick = null,
    onPageChange = null,
    className = '',
    style = {},
    searchComponent = null,
    ...props
}) => {
    const isLoadingState = loading || isLoading;

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);

    useEffect(() => {
        setCurrentPage(1);
    }, [data.length, itemsPerPage]);

    const totalRows = data.length;
    const totalPages = Math.ceil(totalRows / itemsPerPage) || 1;
    const activePage = Math.min(Math.max(1, currentPage), totalPages);

    const paginatedData = useMemo(() => {
        const startIndex = (activePage - 1) * itemsPerPage;
        return data.slice(startIndex, startIndex + itemsPerPage);
    }, [data, activePage, itemsPerPage]);

    const canGoBack = activePage > 1;
    const canGoForward = activePage < totalPages;

    const cellStyle = (col) => {
        const width = col.width || col.style?.width;
        const minWidth = col.minWidth || col.style?.minWidth || 0;
        const maxWidth = col.maxWidth || col.style?.maxWidth;
        return {
            width: width || undefined,
            flex: width ? `0 0 ${width}` : col.flex ? `${col.flex} ${col.flex} 0` : '1 1 0',
            minWidth,
            maxWidth: maxWidth || undefined,
            display: 'flex',
            alignItems: 'center',
            overflow: col.overflow ?? 'hidden',
            justifyContent: col.align === 'center' ? 'center' : col.align === 'right' ? 'flex-end' : 'flex-start',
        };
    };

    return (
        <div className={`neo-table-wrapper ${className}`} style={style} {...props}>
            <NeoLoader type="overlay" loading={isLoadingState} label="Atualizando tabela..." />

            {/* TOPBAR: seletor de itens por página + search alinhados */}
            {(data.length > 0 || searchComponent) && (
                <div className="neo-table-topbar">
                    {data.length > 0 && (
                        <div className="d-flex align-items-center gap-2">
                            <span className="neo-table-topbar-label">Mostrar</span>
                            <NeoInput
                                type="select"
                                value={itemsPerPage}
                                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                options={itemsPerPageOptions.map((opt) => ({ value: opt, label: String(opt) }))}
                                size="sm"
                                flat={true}
                                className="neo-table-per-page-select"
                            />
                            <span className="neo-table-topbar-label">registros por página</span>
                        </div>
                    )}
                    {searchComponent && (
                        <div className="neo-table-topbar-search">
                            {searchComponent}
                        </div>
                    )}
                </div>
            )}

            {/* HEADER */}
            {columns.length > 0 && data.length > 0 && (
                <div className="neo-table-header-row">
                    {columns.map((col, idx) => (
                        <div key={idx} style={cellStyle(col)} className="neo-table-header-cell">
                            {col.label}
                        </div>
                    ))}
                </div>
            )}

            {/* ROWS */}
            <div className="d-flex flex-column">
                {paginatedData.length > 0 ? (
                    paginatedData.map((row, rowIndex) => {
                        const rowKey = row[keyField] || rowIndex;
                        return (
                            <div
                                key={rowKey}
                                className={`neo-table-row${onRowClick ? ' neo-table-row-clickable' : ''}`}
                                onClick={() => onRowClick && onRowClick(row)}
                            >
                                {columns.map((col, idx) => (
                                    <div key={idx} style={cellStyle(col)}>
                                        {col.render
                                            ? col.render(row, rowIndex, { page: activePage, rowsPerPage: itemsPerPage })
                                            : (row[col.key] !== undefined && row[col.key] !== null ? String(row[col.key]) : '—')
                                        }
                                    </div>
                                ))}
                            </div>
                        );
                    })
                ) : (
                    <div className="neo-table-empty-row">
                        <NeoEmptyState
                            icon="mdi-table-off"
                            title={emptyMessage}
                            description={emptySubMessage}
                        />
                    </div>
                )}
            </div>

            {/* FOOTER */}
            {data.length > 0 && (
                <div className="neo-table-footer">
                    <div className="neo-table-pagination-info">
                        Exibindo {(activePage - 1) * itemsPerPage + 1}–{Math.min(activePage * itemsPerPage, totalRows)} de {totalRows}
                    </div>
                    <div className="d-flex gap-1 align-items-center">
                        <NeoButton
                            variant="outline-chocolate"
                            size="sm"
                            square
                            disabled={!canGoBack}
                            onClick={() => {
                                const newPage = activePage - 1;
                                setCurrentPage(newPage);
                                if (onPageChange) onPageChange(newPage, itemsPerPage);
                            }}
                            icon="mdi-chevron-left"
                            title="Página Anterior"
                        />
                        <span className="neo-table-page-indicator">
                            {activePage} / {totalPages}
                        </span>
                        <NeoButton
                            variant="outline-chocolate"
                            size="sm"
                            square
                            disabled={!canGoForward}
                            onClick={() => {
                                const newPage = activePage + 1;
                                setCurrentPage(newPage);
                                if (onPageChange) onPageChange(newPage, itemsPerPage);
                            }}
                            icon="mdi-chevron-right"
                            title="Próxima Página"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

NeoTable.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string,
        label: PropTypes.string.isRequired,
        render: PropTypes.func,
        width: PropTypes.string,
        align: PropTypes.string
    })).isRequired,
    data: PropTypes.array,
    keyField: PropTypes.string,
    loading: PropTypes.bool,
    isLoading: PropTypes.bool,
    emptyMessage: PropTypes.string,
    emptySubMessage: PropTypes.string,
    itemsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
    defaultItemsPerPage: PropTypes.number,
    onRowClick: PropTypes.func,
    onPageChange: PropTypes.func,
    className: PropTypes.string,
    style: PropTypes.object,
    searchComponent: PropTypes.node
};

export default NeoTable;
